'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import { alpha } from '@mui/material/styles';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface NumericRangeFilterProps {
	fieldLabel: string;
	min: number;
	max: number;
	step?: number;
	minDistance?: number;
	initialRange?: [number | undefined, number | undefined];
	id: string;
	disabled?: boolean;
	unitOfMeasure: string;
	onChange: (range: [number | undefined, number | undefined]) => void;
	withLabelBackground?: boolean;
}

const NumericRangeFilter = ({
	fieldLabel,
	min,
	max,
	step = 10,
	minDistance = 1,
	initialRange = [min, max],
	id,
	disabled = false,
	unitOfMeasure,
	onChange,
	withLabelBackground,
}: Readonly<NumericRangeFilterProps>) => {
	const [range, setRange] = useState<[number | undefined, number | undefined]>(initialRange);
	const [open, setOpen] = useState(false);

	const fieldLabelId = `${id}-label`;

	const marks = [
		{
			value: min,
			label: `${min} ${unitOfMeasure}`,
		},
		{
			value: max,
			label: `${max} ${unitOfMeasure}`,
		},
	];

	const handleSliderChange = (_: Event, newValue: number | number[], activeThumb: number) => {
		if (!Array.isArray(newValue)) return;

		let [newFrom, newTo] = newValue as [number, number];

		// Clamp both ends to valid step
		newFrom = Math.round(newFrom / step) * step;
		newTo = Math.round(newTo / step) * step;

		if (newTo - newFrom < minDistance) {
			if (activeThumb === 0) {
				const clamped = Math.min(newFrom, max - minDistance);
				newFrom = clamped;
				newTo = clamped + minDistance;
			} else {
				const clamped = Math.max(newTo, minDistance);
				newFrom = clamped - minDistance;
				newTo = clamped;
			}
		}

		const clampedRange: [number, number] = [newFrom, newTo];

		setRange(clampedRange);
		onChange(clampedRange);
	};

	const handleClose = () => {
		setRange([undefined, undefined]);
		onChange([undefined, undefined]);
	};

	const hasValidRange = typeof range[0] === 'number' && typeof range[1] === 'number' && range[0] < range[1];
	const isRangeChanged = hasValidRange && (range[0] !== min || range[1] !== max);

	const getDisplayValue = () => {
		if (!hasValidRange || !isRangeChanged) return undefined;
		return `${fieldLabel}: ${range[0]} - ${range[1]} ${unitOfMeasure}`;
	};

	const withBackground = (open || isRangeChanged) && withLabelBackground;

	return (
		<FormControl fullWidth>
			<InputLabel
				id={fieldLabelId}
				shrink={open || isRangeChanged}
				sx={(theme) => ({
					background:
						withBackground && `linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, ${theme.palette.lightBackground.main} 100%)`,
					backgroundColor: !withLabelBackground && 'background.paper',
					px: 0.7,
					color: 'grey.dark',
					'&.Mui-focused': {
						color: 'grey.dark',
					},
				})}
			>
				{fieldLabel}
			</InputLabel>
			<Select
				labelId={fieldLabelId}
				id={id}
				value=""
				input={<OutlinedInput notched={false} />}
				displayEmpty
				renderValue={getDisplayValue}
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				sx={{
					color: (theme) => `${alpha(theme.palette.black.main, 0.87)}`,
					'& .MuiSelect-iconOutlined': { display: isRangeChanged ? 'none' : '', mr: 1, color: 'primary.main' },
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: 'grey.dark',
					},
					backgroundColor: 'white.main',
				}}
				IconComponent={ExpandMoreIcon}
				MenuProps={{
					PaperProps: {
						sx: {
							mt: '1px', // margin-top of the menu to separate from select
						},
					},
				}}
				data-testid={`numeric-range-filter-${id}`}
				endAdornment={
					isRangeChanged && (
						<IconButton onClick={handleClose}>
							<HighlightOffIcon />
						</IconButton>
					)
				}
			>
				<MenuItem
					disableRipple
					disableTouchRipple
					disableGutters
					sx={{
						mt: '1px',
						p: 0,
						'&:hover': {
							backgroundColor: 'transparent',
							boxShadow: 'none',
						},
						'&.Mui-focusVisible': {
							backgroundColor: 'transparent',
							boxShadow: 'none',
						},
					}}
				>
					<Box
						sx={{ mx: '24px', px: '16px', pt: '24px', width: '100%' }}
						onClick={(e) => e.stopPropagation()}
						onMouseDown={(e) => e.stopPropagation()}
					>
						<Slider
							value={[range[0] ?? min, range[1] ?? max]}
							onChange={handleSliderChange}
							valueLabelDisplay="auto"
							min={min}
							max={max}
							step={step}
							marks={marks}
							disabled={disabled}
							disableSwap
							sx={{
								color: 'secondary.main',
								'& .MuiSlider-thumb': {
									'&:focus, &:hover, &.Mui-active': {
										boxShadow: (theme) => `0 0 0 8px ${alpha(theme.palette.secondary.main, 0.16)}`,
									},
								},
								'& .MuiSlider-mark': {
									backgroundColor: 'secondary.main',
								},
								'& .MuiSlider-markLabel': {
									color: (theme) => `${alpha(theme.palette.black.main, 0.87)}`,
								},
								'& .MuiSlider-valueLabel': {
									backgroundColor: 'grey.ultraDark',
								},
								'& .MuiSlider-rail': {
									backgroundColor: 'primary.main',
									opacity: 0.32,
								},
							}}
						/>
					</Box>
				</MenuItem>
			</Select>
		</FormControl>
	);
};

export default NumericRangeFilter;
